import React, {useState, useEffect} from "react";
import Stockchart from "../../../components/Stockchart";
import {useDataWithRoute, useTitleFromComponent} from "../../../utils/hooks";
import {VsiBubble, VsiH4} from "../../../styled/App.styled";
import {Card} from "reactstrap";
import Grid from "../../../components/Grid";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import {useSearchParams} from "react-router-dom";
import {capitalizeWord, formatStockChartConfig, toSnakeCase} from "../../../utils/Helpers";
import {genericColors, enterpriseTrendColors} from "../../../utils/chartUtils";
import {useVsiAppContext} from "../../../stores/VsiApp";
import { numberFormatter } from "../../../utils/gridFormatters";

const chartMetrics = [
    {value: 'all_cluster', label: 'All Clusters', route: '_by_all_clusters_proc', yAxis: 'GiB'},
    {value: 'by_cluster', label: 'By Cluster', route: '_by_cluster_proc', yAxis: 'GiB'},
    {value: 'all_node', label: 'All Nodes', route: '_by_all_nodes_proc', yAxis: 'GiB'},
    {value: 'by_node', label: 'By Node', route: '_by_node_proc', yAxis: 'GiB'},
    {value: 'all_pod', label: 'All Pods', route: '_by_all_pods_proc', yAxis: 'GiB'},
    {value: 'by_pod', label: 'By Pod', route: '_by_pod_proc', yAxis: 'GiB'},
    // {value: 'all_container', label: 'All Containers', route: '_by_all_containers_proc', yAxis: 'GiB'},
    {value: 'by_container', label: 'By Container', route: '_by_container_proc', yAxis: 'GiB'},
];

export default function KubernetesCapacityPlan({title, context}) {
    useTitleFromComponent(`${context} / ${title}`);
    const { theme, client, periodId } = useVsiAppContext();

    const [searchParams, setSearchParams] = useSearchParams();

    const metric = searchParams.get('metric') || 'all_business_units';
    let defaultMetric = chartMetrics.find(({value}) => metric === value) || chartMetrics[0];

    const [chartMetric, setChartMetric] = useState(defaultMetric);

    const [chartData, setChartData] = useState([]);
    const [chartDefs, setChartDefs] = useState([]);
    const [formattedChartData, setFormattedChartData] = useState([]);
    const [chartConfig, setChartConfig] = useState({
        colors: genericColors
    });

    const [additionalChartOption, setAdditionalChartOption] = useState({});
    const [additionalChartOptionSelection, setAdditionalChartOptionSelection] = useState([]);
    const [additionalChartOptionLabel, setAdditionalChartOptionLabel] = useState('');

    const getRouteForSelection = route => {
        if (searchParams.get('type')) {
            if (!searchParams.get('metric') || searchParams.get('metric') === 'all_business_units') {
                route = '_by_all_business_units';
            } else {
                route = `_${searchParams.get('metric')}`;
            }
            route += '_proc';
        }
        let routeToSearch = `/api/v6/report/kubernetes_trends${route}?client_id=${client.clientId}&period_id=${periodId}`;

        if (chartMetric.value === 'by_cluster' && searchParams.get('cluster')) {
            routeToSearch += `&cluster_name=${searchParams.get('cluster')}`;
        } else if (chartMetric.value === 'by_node' && searchParams.get('node')) {
            routeToSearch += `&node_name=${searchParams.get('node')}`;
        } else if (chartMetric.value === 'by_pod' && searchParams.get('pod')) {
            routeToSearch += `&pod_name=${searchParams.get('pod')}`;
        } else if (chartMetric.value === 'by_container' && searchParams.get('container')) {
            routeToSearch += `&container_name=${searchParams.get('container')}`;
        }

        return routeToSearch;
    };

    const [data, {isLoading}] = useDataWithRoute(getRouteForSelection(chartMetric.route))

    useEffect(() => {
        setChartData(data?.recordsets[0]);
        handleStateWithData(data?.recordsets || [[], []]);
    }, [data, searchParams.get(toSnakeCase(additionalChartOptionLabel)), chartMetrics]);

    const onMetricChange = async (metric) => {
        setChartMetric(metric);
        searchParams.set('metric', metric.value);
        searchParams.delete('cluster');
        searchParams.delete('node');
        searchParams.delete('pod');
        searchParams.delete('container');
        setSearchParams(searchParams, {replace: true});
        getRouteForSelection(metric.route);
    };

    const onAdditionalOptionChange = async (option) => {
        setAdditionalChartOption(option);
        searchParams.set(toSnakeCase(additionalChartOptionLabel), option.value);
        setSearchParams(searchParams, {replace: true});
    };

    const setAllAdditionalParams = (selection, metric) => {
        let label = additionalChartOptionLabel;
        let select = [...additionalChartOptionSelection];
        let addtl = {...additionalChartOption};
        if (additionalChartOptionLabel !== metric) {
            label = metric;
        }
        if (additionalChartOptionLabel === '' || additionalChartOptionLabel !== metric || selection.length > 0) {
            select = [...selection];
            addtl = {...select[0]};
        }
        let search = searchParams.get(toSnakeCase(metric));
        if (search !== addtl.value) {
            addtl = {...select.find(({value}) => value === search) || addtl}
        }

        setAdditionalChartOptionSelection(select);
        setAdditionalChartOptionLabel(label);
        setAdditionalChartOption(addtl);
    };

    const handleStateWithData = data => {
        let config = null;
        let griDefs = [
            {
                "field": "formatted_collection_date",
                "headerName": "Collection Date"
            },
            {
                "field": "total_cpu",
                "headerName": "CPU",
                "cellRenderer": numberFormatter
            },
            {
                "field": "total_memory",
                "headerName": "Memory",
                "cellRenderer": numberFormatter
            },
            {
                "field": "total_disk",
                "headerName": "Disk",
                "cellRenderer": numberFormatter
            }
        ];
        let sortedData = data?.[0]?.sort((a, b) => new Date(a.collection_date) - new Date(b.collection_date));

        if (chartMetric.value.includes("by")) {
            let typeLines = [
                {
                    name: 'CPU Usage',
                    field: 'total_cpu',
                    dataLabels: {format: '{point.y:,.0f}'},
                    tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                },
                {
                    name: 'Memory Usage',
                    field: 'total_memory',
                    dataLabels: {format: '{point.y:,.0f}'},
                    tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                },
                {
                    name: 'Disk Usage',
                    field: 'total_disk',
                    dataLabels: {format: '{point.y:,.0f}'},
                    tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'},
                }
            ]
            config = formatStockChartConfig(sortedData || [], typeLines)
            let chartMetricSplit = chartMetric.value.split("_")
            setAllAdditionalParams((data?.[1] || []).map(datum => {
                return {
                    value: datum[chartMetricSplit[1]],
                    label: datum[chartMetricSplit[1]],
                    route: `${chartMetricSplit[1]}_name=${datum[chartMetricSplit[1]]}`
                }
            }), capitalizeWord(chartMetricSplit[1]));

            setChartConfig({
                colors: enterpriseTrendColors,
                yAxis: {
                    title: {
                        text: 'GiB'
                    },
                }
            });

            setChartDefs(griDefs);
            setFormattedChartData(config.series);
        } else if (chartMetric.value.includes("all")) {
            griDefs = [{
                "field": `${chartMetric.value.split("_")[1]}_name`,
                "headerName": `${capitalizeWord(chartMetric.value.split("_")[1])}`,
                "sort": 'asc'
            }].concat(griDefs)

            let findUnit = {};
            let chartMetricSplit = chartMetric.value.split("_")[1]
            const foundUnitData = data[0]?.map(datum => {
                if(chartMetricSplit === 'container') {
                    if (!findUnit[datum[`${chartMetricSplit}`]]) {
                        findUnit[datum[`${chartMetricSplit}`]] = true;
                    }
                } else {
                    if (!findUnit[datum[`${chartMetricSplit}_name`]]) {
                        findUnit[datum[`${chartMetricSplit}_name`]] = true;
                    }
                }
                const returnDatum = {}

                for (let dtKey in datum) {
                    // check if in the key (no prototypes) and if it's capitalized
                    if (datum.hasOwnProperty(dtKey)) {
                        // lowercasing...
                        returnDatum[!dtKey.charAt(0).match(/[A-Z\u00C0-\u00DC]+/) ? dtKey : dtKey.toLowerCase()] = datum[dtKey]
                    }
                }
                return returnDatum;
            });
            const unitFields = Object.keys(findUnit).map(unit => ({
                name: unit,
                field: 'total_cpu',
                dataLabels: {format: `{point.y:,.0f}`},
                tooltip: {pointFormat: `{series.name}: <b>{point.y:,.0f}</b>`},
            }));
            config = formatStockChartConfig(foundUnitData || [], unitFields, `${chartMetricSplit}_name`)
            setChartConfig({
                ...chartConfig,
                colors: enterpriseTrendColors,
                yAxis: {
                    title: {
                        text: 'GiB'
                    },
                }
            });

            setChartDefs(griDefs);
            setFormattedChartData(config?.series);
            setAllAdditionalParams([], '');
        }
    };

    const filterChanged = (eventData) => {
        let filteredData = [];
        eventData.api.forEachNodeAfterFilter(node => {
            if (node.data) filteredData.push(node.data);
        });

        handleStateWithData([filteredData]);
    };

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>{`Kubernetes Trend ${chartMetric.label}`}</VsiH4>
            <VsiBubble noAutobot>
                <div className={"w-100 pb-2 d-flex"}>
                    <VsiLabeledSelect options={chartMetrics}
                                      isDisabled={isLoading}
                                      label={'Metric'}
                                      minWidth={200}
                                      maxWidth={500}
                                      onChange={onMetricChange}
                                      defaultValue={chartMetric}/>
                    {additionalChartOptionLabel && (
                        <VsiLabeledSelect options={additionalChartOptionSelection}
                                        isDisabled={isLoading}
                                        label={additionalChartOptionLabel}
                                        minWidth={200}
                                        maxWidth={600}
                                        onChange={onAdditionalOptionChange}
                                        defaultValue={additionalChartOption}/>
                    )}
                </div>
            </VsiBubble>

            <VsiBubble
                unit={chartMetric.value}
                // autobotInfo={{
                //     secondaryMetric: [chartMetric.value, additionalChartOption.value],
                //     yAxes: chartConfig.yAxis
                // }}
            >
                <Stockchart series={formattedChartData} config={chartConfig}/>
            </VsiBubble>
            <VsiBubble
                // unit={chartType.value}
                autobotInfo={{secondaryMetric: [chartMetric.value, additionalChartOption.value]}}>
                <Grid
                    addStyle
                    data={chartData}
                    columnDefs={chartDefs}
                    saveParams={{
                        metric: chartMetric.value,
                    }}
                    filterChanged={filterChanged}/>
            </VsiBubble>

        </Card>
    );
}
